import React, { ChangeEvent } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment } from '@mui/material';

import { TextField } from './textfield.component';
import { PasswordProps } from '../../interfaces';
import { getTextFieldError } from '../../validations';
import { useValidateInput, useFormContext, useGetFieldRules } from '../../hooks';
import { passwordReExp } from '../../utils';
import { TooltipContainer } from '../tooltip-container';

export function Password({ name, ...props }: PasswordProps): JSX.Element {
  const contextRules = useGetFieldRules<PasswordProps>(name);
  return <PasswordComponent {...{ ...contextRules, ...props }} name={name} />;
}

function PasswordComponent(props: PasswordProps): JSX.Element {
  const getError = (value: unknown): string[] | null => {
    const errorMessages = props.customFieldProps?.errorMessages;
    if (typeof value === 'string' && !passwordReExp.test(value)) {
      return [errorMessages?.invalidPassword ?? 'Follow Password Criteria'];
    }

    const fieldError = getTextFieldError(value, props, 'characters', errorMessages);
    return fieldError ? [fieldError] : null;
  };

  const { error, validateInput } = useValidateInput(props, getError);
  const { setValueByName } = useFormContext();
  const allProps = { ...props, tooltip: '' };
  const [typeInput, setTypeInput] = React.useState('password');
  const setValue = (value: string | number): void => {
    setValueByName(props.name, value);
    if (props.onChange) {
      props.onChange(value);
    }
    validateInput(value);
  };

  const ValueVisibleButton = (): JSX.Element => {
    if (typeInput === 'password') {
      return (
        <IconButton onClick={(): void => setTypeInput('text')} aria-label='show'>
          <Visibility />
        </IconButton>
      );
    }
    return (
      <IconButton onClick={(): void => setTypeInput('password')} aria-label='hide'>
        <VisibilityOff />
      </IconButton>
    );
  };
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValue(event.target.value);
  };

  React.useEffect(() => {
    if (props.defaultValue && props.defaultValue !== '') {
      setValue(props.defaultValue);
    }
    if (props.defaultValue != null) validateInput(props.defaultValue);
  }, []);

  return (
    <TooltipContainer tooltip={props.tooltip}>
      <TextField
        {...allProps}
        muiProps={{
          name: props.name,
          value: props.value,
          onChange: handleChange,
          helperText: error ? error : props.helperText,
          error: error != null,
          type: typeInput,
          InputProps: {
            endAdornment: (
              <InputAdornment position='end'>
                <ValueVisibleButton />
              </InputAdornment>
            )
          }
        }}
      />
    </TooltipContainer>
  );
}
